'use client';

import useMediaQuery from '@/hooks/useMediaQuery';
import useScrollTrigger from '@/hooks/useScrollTrigger';
import clsx from 'clsx';
import { useCallback, useRef, useState, useEffect } from 'react';

import styles from './Popover.module.css';
import { PopoverProps } from './types';

const Popover = ({
  id,
  anchorComponent,
  popoverContent,
  showShadow = true,
  containerClassName,
  contentClassName,
  headerHeight,
  isHeaderPopover = false,
}: PopoverProps) => {
  const [isPopoverOpen, setIsPopoverOpen] = useState<boolean>(false);
  const openRef = useRef<boolean>(false);
  const open = Boolean(isPopoverOpen);
  const contentClasses: string = clsx(styles.content, { [styles.contentShadow]: showShadow }, contentClassName);
  const scrollPage: string | boolean = useScrollTrigger({ threshold: headerHeight });
  const mobile: boolean = useMediaQuery('(max-width:1279px)');
  const popupRef = useRef<HTMLDivElement>(null);

  // in order to prevent a closure in handleClose had to use useRef and the function
  const setClose = () => (openRef.current = true);

  useEffect(() => {
    window.addEventListener('mousedown', handleClickOutside);
    return () => {
      window.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  const handleClickOutside = ({ target }: MouseEvent): void => {
    if (!popupRef.current?.contains(target as Node)) {
      setIsPopoverOpen(false);
    }
  };

  const handleClose = useCallback(() => {
    setClose();
    setTimeout(() => {
      if (openRef.current) {
        setIsPopoverOpen(false);
      }
    }, 1000);
  }, []);

  const handleOpen = () => {
    openRef.current = false;
    setIsPopoverOpen(true);
  };

  useEffect(() => {
    if (scrollPage) {
      setIsPopoverOpen(false);
    }
  }, [scrollPage]);

  const mobileHeaderStyles = mobile ? `${headerHeight}px` : '3rem';
  const contentStyles = isHeaderPopover ? { top: mobileHeaderStyles } : { top: 'auto' };

  return (
    <div className={clsx(styles.container, containerClassName)} ref={popupRef} id={id} data-testid={id}>
      <div
        onClick={() => setIsPopoverOpen((prevState) => !prevState)}
        className={styles.anchor}
        onMouseEnter={handleOpen}
        onMouseLeave={handleClose}
        data-testid={`popover-anchor-${id}`}
      >
        {typeof anchorComponent === 'function' ? anchorComponent(open) : anchorComponent}
      </div>
      {isPopoverOpen && (
        <div
          data-testid={`popover-content-${id}`}
          style={contentStyles}
          className={contentClasses}
          onMouseLeave={handleClose}
          onMouseEnter={handleOpen}
        >
          {typeof popoverContent === 'function' ? popoverContent({ open, close: handleClose }) : popoverContent}
        </div>
      )}
    </div>
  );
};

export default Popover;
